import React, {useRef, useState} from "react";
import axios from "axios";
import {
  generateRandomNumber,
  getAuthToken,
  getClientIdOfUser,
  getLoggedInUserId,
  showToast,
} from "../../utils/utility";

import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faCircleExclamation,
  faCloudArrowUp,
  faFileExcel,
  faSpinner,
  faTrashCan,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";

import * as Constants from "../../utils/Constants";
import "../../styles/components/customer-upload.css";

const ACCEPTED = ".xlsx,.xls";
const MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

const formatSize = (bytes) => {
  if (!bytes) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

const CustomerDetailsUpload = () => {
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState(null); // { ok: boolean, message: string }
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const validateAndSet = (f) => {
    setResult(null);
    if (!f) return;
    const name = f.name.toLowerCase();
    if (!(name.endsWith(".xlsx") || name.endsWith(".xls"))) {
      setResult({ok: false, message: "Only .xlsx or .xls files are allowed."});
      return;
    }
    if (f.size > MAX_SIZE_BYTES) {
      setResult({ok: false, message: `File is too large (max ${formatSize(MAX_SIZE_BYTES)}).`});
      return;
    }
    setFile(f);
  };

  const handleFileChange = (e) => validateAndSet(e.target.files?.[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    validateAndSet(e.dataTransfer.files?.[0]);
  };

  const clearFile = () => {
    setFile(null);
    setResult(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleUpload = async () => {
    if (!file) {
      setResult({ok: false, message: "Please select a file first."});
      return;
    }
    setIsUploading(true);
    setResult(null);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("requestId", generateRandomNumber());
    formData.append("userId", getLoggedInUserId());

    try {
      const response = await axios.post(
        `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/customers/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `${Constants.BEARER} ${getAuthToken()}`,
          },
        }
      );
      const msg = response.data?.responseMessage || "Customers uploaded successfully.";
      setResult({ok: true, message: msg});
      showToast(msg, {type: "success"});
      clearFile();
    } catch (error) {
      const msg =
        error?.response?.data?.responseMessage ||
        error?.message ||
        "Upload failed. Please check the file and try again.";
      setResult({ok: false, message: msg});
      showToast(msg, {type: "error"});
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="max-w-[840px] mx-auto pb-8">
      <div className="bg-white rounded-xl border border-ink-200 shadow-xs">
        <div className="p-6 border-b border-ink-100 flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-brand-50 grid place-items-center shrink-0">
            <FontAwesomeIcon icon={faCloudArrowUp} className="text-brand-700" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-ink-900">Upload customer list</h2>
            <p className="text-sm text-ink-500 mt-1">
              Bulk-import customers from an Excel file. Only <code className="font-mono text-xs px-1 bg-ink-100 rounded">.xlsx</code> /{" "}
              <code className="font-mono text-xs px-1 bg-ink-100 rounded">.xls</code> formats are supported.
            </p>
          </div>
        </div>

        <div className="p-6 space-y-5">
          <div className="rounded-lg bg-warning-50 border border-warning-100 px-4 py-3 text-sm text-warning-900">
            <div className="flex items-center gap-2 font-semibold mb-2">
              <FontAwesomeIcon icon={faTriangleExclamation} className="text-warning-600" />
              Required columns (in order)
            </div>
            <ol className="ml-6 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 list-decimal text-warning-900">
              <li>Customer Name</li>
              <li>Address</li>
              <li>Latitude</li>
              <li>Longitude</li>
              <li>Email ID</li>
              <li>Phone Number</li>
            </ol>
          </div>

          <div
            className={
              "rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors duration-fast " +
              (dragging
                ? "border-brand-500 bg-brand-50"
                : file
                ? "border-success-300 bg-success-50/50"
                : "border-ink-300 bg-ink-50 hover:border-brand-400 hover:bg-brand-50/40")
            }
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => !file && inputRef.current?.click()}
            role="button"
            tabIndex={0}
          >
            <input ref={inputRef} type="file" accept={ACCEPTED} onChange={handleFileChange} hidden />
            {!file ? (
              <>
                <div className="h-12 w-12 mx-auto rounded-full bg-white border border-ink-200 grid place-items-center mb-3">
                  <FontAwesomeIcon icon={faCloudArrowUp} className="text-brand-700 text-lg" />
                </div>
                <p className="text-base font-medium text-ink-900">
                  Drop your Excel file here, or{" "}
                  <span className="text-brand-700 underline">browse</span>
                </p>
                <p className="text-xs text-ink-500 mt-1">
                  Accepted: {ACCEPTED} · Max size {formatSize(MAX_SIZE_BYTES)}
                </p>
              </>
            ) : (
              <div
                className="inline-flex items-center gap-3 bg-white border border-success-300 rounded-lg px-3 py-2 shadow-xs"
                onClick={(e) => e.stopPropagation()}
              >
                <FontAwesomeIcon icon={faFileExcel} className="text-success-600 text-lg" />
                <div className="text-left min-w-0">
                  <div className="text-sm font-medium text-ink-900 truncate max-w-[280px]" title={file.name}>
                    {file.name}
                  </div>
                  <div className="text-xs text-ink-500">{formatSize(file.size)}</div>
                </div>
                <button
                  type="button"
                  onClick={clearFile}
                  title="Remove file"
                  disabled={isUploading}
                  className="inline-flex items-center justify-center h-7 w-7 rounded-md text-danger-600 hover:bg-danger-50 disabled:opacity-50"
                >
                  <FontAwesomeIcon icon={faTrashCan} />
                </button>
              </div>
            )}
          </div>

          {result && (
            <div
              className={
                "rounded-lg border px-4 py-3 text-sm flex items-center gap-2 " +
                (result.ok
                  ? "bg-success-50 border-success-100 text-success-700"
                  : "bg-danger-50 border-danger-100 text-danger-700")
              }
            >
              <FontAwesomeIcon icon={result.ok ? faCheckCircle : faCircleExclamation} />
              <span>{result.message}</span>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-ink-100 flex justify-end gap-2">
          <button
            type="button"
            onClick={clearFile}
            disabled={!file || isUploading}
            className="inline-flex items-center h-9 px-4 text-sm font-medium rounded-md bg-white border border-ink-200 text-ink-700 hover:bg-ink-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={handleUpload}
            disabled={!file || isUploading}
            className="inline-flex items-center gap-2 h-9 px-4 text-sm font-medium rounded-md bg-accent-500 text-white hover:bg-accent-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isUploading ? (
              <><FontAwesomeIcon icon={faSpinner} spin /> Uploading…</>
            ) : (
              <><FontAwesomeIcon icon={faCloudArrowUp} /> Upload</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerDetailsUpload;
